<script>
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import SettingsSection from '../../../../../components/SettingsSection.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { CONVERSATION_PRIORITY } from 'shared/constants/messages';

export default {
  components: {
    SettingsSection,
    NextButton,
  },
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      automaticPriorityMode: 'off', // 'off', 'all_messages', 'first_message'
      priorityConfigs: {
        [CONVERSATION_PRIORITY.URGENT]: {
          enabled: false,
          description: '',
          default: false,
        },
        [CONVERSATION_PRIORITY.HIGH]: {
          enabled: false,
          description: '',
          default: false,
        },
        [CONVERSATION_PRIORITY.MEDIUM]: {
          enabled: false,
          description: '',
          default: false,
        },
        [CONVERSATION_PRIORITY.LOW]: {
          enabled: false,
          description: '',
          default: false,
        },
      },
      isUpdating: false,
    };
  },
  computed: {
    priorityLevels() {
      return [
        {
          key: CONVERSATION_PRIORITY.URGENT,
          label: this.$t('CONVERSATION.PRIORITY.OPTIONS.URGENT'),
          color: 'text-red-600',
        },
        {
          key: CONVERSATION_PRIORITY.HIGH,
          label: this.$t('CONVERSATION.PRIORITY.OPTIONS.HIGH'),
          color: 'text-orange-600',
        },
        {
          key: CONVERSATION_PRIORITY.MEDIUM,
          label: this.$t('CONVERSATION.PRIORITY.OPTIONS.MEDIUM'),
          color: 'text-yellow-600',
        },
        {
          key: CONVERSATION_PRIORITY.LOW,
          label: this.$t('CONVERSATION.PRIORITY.OPTIONS.LOW'),
          color: 'text-green-600',
        },
      ];
    },
    hasEnabledPriorities() {
      return Object.values(this.priorityConfigs).some(config => config.enabled);
    },
    defaultPriorityCount() {
      return Object.values(this.priorityConfigs).filter(
        config => config.default
      ).length;
    },
  },
  watch: {
    inbox() {
      this.setDefaults();
    },
  },
  mounted() {
    this.setDefaults();
  },
  methods: {
    setDefaults() {
      const priorityConfig = this.inbox?.automatic_priority_config || {};
      this.automaticPriorityMode = priorityConfig.mode || 'off';
      Object.keys(this.priorityConfigs).forEach(priority => {
        const config = priorityConfig.priorities?.[priority] || {};
        this.priorityConfigs[priority] = {
          enabled: config.enabled || false,
          description: config.description || '',
          default: config.default || false,
        };
      });
    },
    handlePriorityToggle(priority) {
      if (!this.priorityConfigs[priority].enabled) {
        this.priorityConfigs[priority].default = false;
      }
    },
    handleDefaultToggle(priority) {
      if (this.priorityConfigs[priority].default) {
        Object.keys(this.priorityConfigs).forEach(p => {
          if (p !== priority) {
            this.priorityConfigs[p].default = false;
          }
        });
        this.priorityConfigs[priority].description = '';
      }
    },
    async updateInbox() {
      this.isUpdating = true;
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          automatic_priority_config: {
            mode: this.automaticPriorityMode,
            priorities: this.priorityConfigs,
          },
        };
        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(error.message || this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
      this.isUpdating = false;
    },
  },
};
</script>

<template>
  <div>
    <SettingsSection
      :title="$t('INBOX_MGMT.AUTOMATIC_PRIORITY.TITLE')"
      :sub-title="$t('INBOX_MGMT.AUTOMATIC_PRIORITY.SUB_TEXT')"
    >
      <div class="space-y-4">
        <div class="flex items-center">
          <input
            id="priority-off"
            v-model="automaticPriorityMode"
            value="off"
            type="radio"
            class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
          />
          <label
            for="priority-off"
            class="ml-3 block text-sm font-medium text-gray-700"
          >
            {{ $t('INBOX_MGMT.AUTOMATIC_PRIORITY.MODE.OFF') }}
          </label>
        </div>

        <div class="flex items-center">
          <input
            id="priority-all-messages"
            v-model="automaticPriorityMode"
            value="all_messages"
            type="radio"
            class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
          />
          <label
            for="priority-all-messages"
            class="ml-3 block text-sm font-medium text-gray-700"
          >
            {{ $t('INBOX_MGMT.AUTOMATIC_PRIORITY.MODE.ALL_MESSAGES') }}
          </label>
        </div>

        <div class="flex items-center">
          <input
            id="priority-first-message"
            v-model="automaticPriorityMode"
            value="first_message"
            type="radio"
            class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
          />
          <label
            for="priority-first-message"
            class="ml-3 block text-sm font-medium text-gray-700"
          >
            {{ $t('INBOX_MGMT.AUTOMATIC_PRIORITY.MODE.FIRST_MESSAGE') }}
          </label>
        </div>

        <div class="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div class="flex items-start">
            <div class="flex-shrink-0">
              <svg
                class="h-5 w-5 text-yellow-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
            <div class="ml-3">
              <h3 class="text-sm font-medium text-yellow-800">
                {{
                  $t('INBOX_MGMT.AUTOMATIC_PRIORITY.BILLING_DISCLAIMER.TITLE')
                }}
              </h3>
              <p class="mt-1 text-sm text-yellow-700">
                {{
                  $t('INBOX_MGMT.AUTOMATIC_PRIORITY.BILLING_DISCLAIMER.MESSAGE')
                }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </SettingsSection>

    <SettingsSection
      :title="$t('INBOX_MGMT.AUTOMATIC_PRIORITY.PRIORITY_LEVELS.TITLE')"
      :sub-title="$t('INBOX_MGMT.AUTOMATIC_PRIORITY.PRIORITY_LEVELS.SUB_TEXT')"
    >
      <div class="space-y-6">
        <div
          v-for="priority in priorityLevels"
          :key="priority.key"
          class="priority-config-item"
        >
          <div class="flex items-center gap-2 mb-2">
            <input
              :id="`enable-${priority.key}`"
              v-model="priorityConfigs[priority.key].enabled"
              type="checkbox"
              @change="handlePriorityToggle(priority.key)"
            />
            <label
              :for="`enable-${priority.key}`"
              class="font-medium"
              :class="priority.color"
            >
              {{ priority.label }}
            </label>
          </div>

          <div
            v-if="priorityConfigs[priority.key].enabled"
            class="ml-6 space-y-3"
          >
            <div class="flex items-center gap-2">
              <input
                :id="`default-${priority.key}`"
                v-model="priorityConfigs[priority.key].default"
                type="checkbox"
                @change="handleDefaultToggle(priority.key)"
              />
              <label :for="`default-${priority.key}`" class="text-sm">
                {{ $t('INBOX_MGMT.AUTOMATIC_PRIORITY.SET_AS_DEFAULT') }}
              </label>
            </div>

            <div v-if="!priorityConfigs[priority.key].default">
              <label
                :for="`description-${priority.key}`"
                class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
              >
                {{ $t('INBOX_MGMT.AUTOMATIC_PRIORITY.DESCRIPTION_LABEL') }}
              </label>
              <textarea
                :id="`description-${priority.key}`"
                v-model="priorityConfigs[priority.key].description"
                rows="3"
                class="w-full max-w-2xl resize-none"
                :placeholder="
                  $t('INBOX_MGMT.AUTOMATIC_PRIORITY.DESCRIPTION_PLACEHOLDER', {
                    priority: priority.label,
                  })
                "
              />
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="defaultPriorityCount > 1"
        class="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded"
      >
        <p class="text-sm text-yellow-800">
          {{ $t('INBOX_MGMT.AUTOMATIC_PRIORITY.MULTIPLE_DEFAULTS_WARNING') }}
        </p>
      </div>

      <div class="mt-6">
        <NextButton
          :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
          :is-loading="isUpdating"
          :disabled="!hasEnabledPriorities"
          @click="updateInbox"
        />
      </div>
    </SettingsSection>
  </div>
</template>

<style scoped lang="scss">
@import 'dashboard/assets/scss/variables';
@import 'dashboard/assets/scss/mixins';

.priority-config-item {
  @apply border border-slate-200 dark:border-slate-700 rounded-lg p-4;
}

.settings-item {
  @apply block;
}
</style>
